
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';

export default function ContactPage() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      return;
    }

    if (formData.message.length > 500) {
      alert('Message must be 500 characters or less.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d368aslhl5d9vdijbd20', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead 
        title={t('seo.contact.title')}
        description={t('seo.contact.description')}
      />
      <div className="min-h-screen bg-black text-white">
        <Navigation />
        
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-gray-900 to-black text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ fontFamily: '"Orbitron", sans-serif' }}>{t('contact.hero_title')}</h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto" style={{ fontFamily: '"Exo 2", sans-serif' }}>
                {t('contact.hero_subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-8" style={{ fontFamily: '"Orbitron", sans-serif' }}>{t('contact.get_in_touch')}</h2>
                
                <div className="space-y-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-cyan-500 text-white rounded-full flex-shrink-0">
                      <i className="ri-map-pin-line text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2" style={{ fontFamily: '"Rajdhani", sans-serif' }}>{t('contact.location_title')}</h3>
                      <p className="text-gray-600" style={{ fontFamily: '"Exo 2", sans-serif' }}>{t('contact.location_desc')}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-cyan-500 text-white rounded-full flex-shrink-0">
                      <i className="ri-mail-line text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2" style={{ fontFamily: '"Rajdhani", sans-serif' }}>{t('contact.email_title')}</h3>
                      <p className="text-gray-600" style={{ fontFamily: '"Exo 2", sans-serif' }}>{t('contact.email_desc')}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-cyan-500 text-white rounded-full flex-shrink-0">
                      <i className="ri-music-line text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2" style={{ fontFamily: '"Rajdhani", sans-serif' }}>{t('contact.specialization_title')}</h3>
                      <p className="text-gray-600" style={{ fontFamily: '"Exo 2", sans-serif' }}>{t('contact.specialization_desc')}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-cyan-500 text-white rounded-full flex-shrink-0">
                      <i className="ri-global-line text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2" style={{ fontFamily: '"Rajdhani", sans-serif' }}>{t('contact.website_title')}</h3>
                      <a 
                        href="https://www.jeanclaudebastosmusic.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-cyan-500 hover:text-cyan-600 transition-colors cursor-pointer" style={{ fontFamily: '"Exo 2", sans-serif' }}
                      >
                        jeanclaudebastosmusic.com
                      </a>
                    </div>
                  </div>
                </div>

                <div className="mt-12">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-6" style={{ fontFamily: '"Rajdhani", sans-serif' }}>{t('contact.follow_title')}</h3>
                  <div className="flex space-x-4">
                    <a href="https://soundcloud.com/dj-jean-claude-bastos" target="_blank" rel="noopener noreferrer" className="bg-orange-500 hover:bg-orange-600 text-white w-12 h-12 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                      <i className="ri-soundcloud-fill text-xl"></i>
                    </a>
                    <a href="https://open.spotify.com/artist/7MWynFxiCsINwkfZHunLEU" target="_blank" rel="noopener noreferrer" className="bg-green-500 hover:bg-green-600 text-white w-12 h-12 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                      <i className="ri-spotify-fill text-xl"></i>
                    </a>
                    <a href="https://music.apple.com/za/artist/dj-jean-claude-bastos/1828068550" target="_blank" rel="noopener noreferrer" className="bg-gray-800 hover:bg-gray-900 text-white w-12 h-12 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                      <i className="ri-music-fill text-xl"></i>
                    </a>
                    <a href="https://www.iheart.com/artist/dj-jean-claude-bastos-47509177/" target="_blank" rel="noopener noreferrer" className="bg-red-500 hover:bg-red-600 text-white w-12 h-12 rounded-full flex items-center justify-center transition-colors cursor-pointer">
                      <i className="ri-heart-fill text-xl"></i>
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: '"Orbitron", sans-serif' }}>{t('contact.form_title')}</h2>
                
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: '"Exo 2", sans-serif' }}>
                      {t('contact.form_name')}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors text-sm"
                      placeholder={t('contact.form_name_placeholder')}
                      style={{ fontFamily: '"Exo 2", sans-serif' }}
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: '"Exo 2", sans-serif' }}>
                      {t('contact.form_email')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors text-sm"
                      placeholder={t('contact.form_email_placeholder')}
                      style={{ fontFamily: '"Exo 2", sans-serif' }}
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: '"Exo 2", sans-serif' }}>
                      {t('contact.form_message')}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors text-sm resize-none"
                      placeholder={t('contact.form_message_placeholder')}
                      style={{ fontFamily: '"Exo 2", sans-serif' }}
                    />
                    <div className="text-right text-sm text-gray-500 mt-1" style={{ fontFamily: '"Exo 2", sans-serif' }}>
                      {formData.message.length}/500 {t('contact.form_characters')}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting || !formData.name || !formData.email || !formData.message || formData.message.length > 500}
                    className="w-full bg-cyan-500 hover:bg-cyan-6
                    00 disabled:bg-gray-400 disabled:cursor-not-allowed text-white py-3 px-6 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
                    style={{ fontFamily: '"Rajdhani", sans-serif' }}
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line mr-2 animate-spin"></i>
                        {t('contact.form_sending')}
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line mr-2"></i>
                        {t('contact.form_send')}
                      </>
                    )}
                  </button>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                      <div className="flex items-center" style={{ fontFamily: '"Exo 2", sans-serif' }}>
                        <i className="ri-check-circle-line mr-2"></i>
                        {t('contact.form_success')}
                      </div>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                      <div className="flex items-center" style={{ fontFamily: '"Exo 2", sans-serif' }}>
                        <i className="ri-error-warning-line mr-2"></i>
                        {t('contact.form_error')}
                      </div>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: '"Orbitron", sans-serif' }}>{t('contact.services_title')}</h2>
              <p className="text-xl text-gray-600" style={{ fontFamily: '"Exo 2", sans-serif' }}>
                {t('contact.services_subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  title: t('contact.service_1_title'),
                  description: t('contact.service_1_desc'),
                  icon: 'ri-music-2-line',
                  image: 'https://public.readdy.ai/ai/img_res/615c7b1ca3c4cd9c173d1056ff116899.jpg'
                },
                {
                  title: t('contact.service_2_title'),
                  description: t('contact.service_2_desc'),
                  icon: 'ri-sound-module-line',
                  image: 'https://readdy.ai/api/search-image?query=Modern%20sound%20design%20workspace%20with%20audio%20waveforms%20on%20computer%20screens%2C%20digital%20audio%20workstation%2C%20synthesizer%20keyboards%2C%20headphones%2C%20creative%20audio%20production%20environment%2C%20electronic%20music%20creation&width=400&height=200&seq=service-sound&orientation=landscape'
                },
                {
                  title: t('contact.service_3_title'),
                  description: t('contact.service_3_desc'),
                  icon: 'ri-team-line',
                  image: 'https://readdy.ai/api/search-image?query=Creative%20collaboration%20in%20music%20studio%2C%20artists%20working%20together%20on%20electronic%20music%20production%2C%20teamwork%20in%20recording%20studio%2C%20collaborative%20music%20creation%20process%2C%20modern%20production%20environment&width=400&height=200&seq=service-collab&orientation=landscape'
                }
              ].map((service, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-48 object-cover object-top"
                  />
                  <div className="p-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-cyan-500 text-white rounded-full mb-4">
                      <i className={`${service.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3" style={{ fontFamily: '"Rajdhani", sans-serif' }}>{service.title}</h3>
                    <p className="text-gray-600" style={{ fontFamily: '"Exo 2", sans-serif' }}>{service.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
